import { supabase } from './client';
import { v4 as uuidv4 } from 'uuid';

/**
 * Uploads a profile-related asset to Supabase storage
 * @param file The file to upload
 * @param userId The user ID to associate the file with (can be guest UUID)
 * @param assetType The type of asset ('avatar', 'banner', 'cover', 'message_image', 'listing_image', 'listing_video')
 * @param isAuthenticated Whether the user is authenticated
 * @returns Promise resolving to the URL of the uploaded file
 */
export const uploadProfileAssetToStorage = async (
  file: File,
  userId: string,
  assetType: 'avatar' | 'banner' | 'cover' | 'message_image' | 'listing_image' | 'listing_video',
  isAuthenticated: boolean
): Promise<string> => {
  if (!file || !userId) {
    throw new Error("File and User ID are required");
  }

  const fileExt = file.name.split('.').pop();
  const fileName = `${assetType}-${uuidv4()}.${fileExt}`;

  let bucketName: string;
  let uploadPath: string;

  if (assetType === 'listing_image' && !isAuthenticated) {
    bucketName = 'listings';
    uploadPath = `guest/${userId}/images/${fileName}`;
  } else if (assetType === 'listing_video' && !isAuthenticated) {
    bucketName = 'listings_videos'; // Use the new bucket for guest videos
    uploadPath = `guest/${userId}/videos/${fileName}`;
  } else if (assetType === 'listing_video') {
    bucketName = 'listings_videos'; // Use the new bucket for authenticated user videos
    uploadPath = `${userId}/listings/videos/${fileName}`;
  } else {
    bucketName = 'profiles'; // Default bucket for other authenticated assets
    let userSpecificPath = `${userId}`;
    if (assetType === 'message_image') {
      userSpecificPath = `${userId}/messages`;
    } else if (assetType === 'listing_image') { // Authenticated listing images
      userSpecificPath = `${userId}/listings`;
    }
    uploadPath = `${userSpecificPath}/${fileName}`;
  }

  let contentType = file.type;
  if (!contentType || contentType === 'application/octet-stream') {
    // Image content types
    if (fileExt === 'jpg' || fileExt === 'jpeg') contentType = 'image/jpeg';
    else if (fileExt === 'png') contentType = 'image/png';
    else if (fileExt === 'gif') contentType = 'image/gif';
    else if (fileExt === 'webp') contentType = 'image/webp';
    // Video content types
    else if (fileExt === 'mp4') contentType = 'video/mp4';
    else if (fileExt === 'mov') contentType = 'video/quicktime';
    else if (fileExt === 'avi') contentType = 'video/x-msvideo';
    else if (fileExt === 'webm') contentType = 'video/webm';
    else if (fileExt === 'mkv') contentType = 'video/x-matroska';
  }

  try {
    const { data: bucketData, error: bucketError } = await supabase.storage
      .getBucket(bucketName);
    
    if (bucketError && bucketError.message.includes('not found')) {
      // Buckets 'listings', 'profiles', 'listings_videos' should be created via migrations or dashboard.
      // This dynamic creation is less robust for predefined buckets.
      // For now, we'll keep it, but ideally buckets are pre-configured.
      console.warn(`Bucket ${bucketName} not found, attempting to create. This should ideally be pre-configured.`);
      await supabase.storage.createBucket(bucketName, {
        public: true, 
        allowedMimeTypes: assetType === 'listing_video' 
          ? ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/x-matroska'] 
          : ['image/png', 'image/jpeg', 'image/gif', 'image/webp'],
      });
    } else if (bucketError) {
      console.error(`Bucket retrieval error for ${bucketName}:`, bucketError);
      throw bucketError;
    }

    const { data, error } = await supabase.storage
      .from(bucketName)
      .upload(uploadPath, file, {
        cacheControl: '3600',
        contentType: contentType,
        upsert: true 
      });

    if (error) {
      console.error("Upload error:", error);
      throw error;
    }
    
    const { data: publicUrlData } = supabase.storage
      .from(bucketName)
      .getPublicUrl(uploadPath);
    
    if (!publicUrlData || !publicUrlData.publicUrl) {
      throw new Error("Failed to get public URL");
    }
    
    return publicUrlData.publicUrl;
  } catch (error) {
    console.error("Storage upload error:", error);
    throw error;
  }
};

// Function to delete a profile asset
export const deleteProfileAsset = async (url: string): Promise<boolean> => {
  try {
    // Extract the path from the URL
    // This assumes the URL format from getPublicUrl
    // Example URL: https://<project-ref>.supabase.co/storage/v1/object/public/profiles/<userId>/<assetType>-<uuid>.<ext>
    // Or: https://<project-ref>.supabase.co/storage/v1/object/public/listings/guest/<userId>/images/<assetType>-<uuid>.<ext>
    
    const urlParts = url.split('/');
    // Find bucket name ('profiles' or 'listings')
    let bucketNameIndex = urlParts.findIndex(part => part === 'profiles');
    if (bucketNameIndex === -1) {
      bucketNameIndex = urlParts.findIndex(part => part === 'listings');
    }

    if (bucketNameIndex === -1 || bucketNameIndex + 1 >= urlParts.length) {
      console.error("Invalid URL format for deletion:", url);
      return false;
    }
    
    // The path starts after the bucket name
    const path = urlParts.slice(bucketNameIndex + 1).join('/');
    const bucketName = urlParts[bucketNameIndex];
    
    const { error } = await supabase.storage
      .from(bucketName)
      .remove([path]);

    if (error) {
      console.error("Delete error:", error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Storage delete error:", error);
    return false;
  }
};
